// frontend/src/hooks/useReferenceData.js
//
// Per-dataset hooks for reference data (users, suppliers, countries,
// currencies, location types, calendars) used by
// edit forms in the setup pages. Each is one TanStack `useQuery` exposed as
// `{data, loading, error, refetch}`.
//
//   countries / currencies / location types  →  staleTime Infinity, no focus
//                                                refetch (static reference;
//                                                location types per store)
//   users / suppliers / calendars  →  5 min, focus
//                                                refetch (tab-back picks up
//                                                edits)
//
// AuthContext clears the cache across auth and store boundaries
// (queries/client.js clearAllQueries / clearStoreScopedQueries).

import { useQuery } from '@tanstack/react-query';
import { sessionApi } from '../services/SessionApiService';
import { fetchAllPagesVia } from '../utils/fetchAllPages';
import { useAuth } from '../contexts/AuthContext';
import {
  reference,
  locationTypes as locationTypesKey,
  users as usersKey,
  suppliers as suppliersKey,
  calendars as calendarsKey,
} from '../queries/keys';

const FIVE_MIN = 5 * 60 * 1000;


// Response shape extractor — DRF paginator returns
// `{count, next, previous, results}`; some endpoints return a bare array
// (`pagination_class = None`). Both are handled here so per-dataset queryFn
// don't need to know.
//
// A body that is NEITHER throws rather than resolving to `[]`: every one of
// these lists feeds a picker, and an empty picker is indistinguishable from a
// short one. Thrown from inside the queryFn, so it reaches the caller as
// `error`.
function extractList(json) {
  if (Array.isArray(json)) return json;
  if (Array.isArray(json?.results)) return json.results;
  throw new Error('reference endpoint returned neither an array nor a DRF page');
}


// Shared queryFn factory for UNPAGINATED reference endpoints.
function makeFetcher(endpoint) {
  return async () => {
    const res = await sessionApi.get(endpoint);
    if (!res.ok) throw new Error(`HTTP ${res.status}`);
    return extractList(await res.json());
  };
}


// Shared queryFn factory for PAGINATED reference endpoints — every page, not
// the first one. `fetchAllPagesVia` walks to exhaustion and throws rather than
// returning a short list, so a list too long to load whole reaches the caller
// as `error`, like a transport failure.
function makePagedFetcher(path, label) {
  return () => fetchAllPagesVia(sessionApi, path, { label });
}


// `data` is `[]` until the query has data, so a dropdown always gets an
// array; `loading` and `error` say why it is empty.
function adapt(query) {
  return {
    data: query.data ?? [],
    loading: query.isPending,
    error: query.isError ? query.error : null,
    refetch: query.refetch,
  };
}


// ── Global reference data ───────────────────────────────────────────
// `staleTime: Infinity`: countries / currencies / location types change only
// by rare admin update, which the merchant sees on the next page load.

// `/api/countries/` and `/api/currencies/` are `SharedModelViewSet`, which
// sets `pagination_class = None` (backend/api/views/viewsets.py): each serves
// a bare array of every row. Shape pinned by
// backend/api/test_reference_list_shape.py.
//
// `/api/location-types/` below is the opposite case and goes through
// `makePagedFetcher`: `LocationTypeViewSet` overrides no `pagination_class`,
// so it inherits `DEFAULT_PAGINATION_CLASS = core.pagination.StandardPagination`
// (backend/backend/settings.py) and answers with a page envelope — one page of
// which is not the list.
// `enabled` lets a form that only sometimes needs a list (the item-side
// Suppliers dialog's "new supplier" form) read it when it opens, not on
// every page load that merely mounts the form.
export function useCountries({ enabled = true } = {}) {
  return adapt(useQuery({
    queryKey: reference.countries(),
    queryFn: makeFetcher('/api/countries/'),
    staleTime: Infinity,
    refetchOnWindowFocus: false,
    enabled,
  }));
}

export function useCurrencies({ enabled = true } = {}) {
  return adapt(useQuery({
    queryKey: reference.currencies(),
    queryFn: makeFetcher('/api/currencies/'),
    staleTime: Infinity,
    refetchOnWindowFocus: false,
    enabled,
  }));
}

// The store's location types: served per store, so keyed per store.
export function useLocationTypes() {
  const { selectedStore } = useAuth();
  const storeId = selectedStore?.id;
  return adapt(useQuery({
    queryKey: locationTypesKey.all(storeId),
    queryFn: makePagedFetcher('/api/location-types/', 'location types'),
    staleTime: Infinity,
    refetchOnWindowFocus: false,
    enabled: !!storeId,
  }));
}


// ── Tenant-scoped reference data ────────────────────────────────────
// `/auth/users/` lists the peers of every organization the caller belongs to
// (authentication/views/viewsets.py UserViewSet.get_queryset), whatever store
// is selected, so the key has no storeId. Logout removes every query.

export function useUsers() {
  return adapt(useQuery({
    queryKey: usersKey.all(),
    queryFn: makePagedFetcher('/auth/users/', 'users'),
    staleTime: FIVE_MIN,
  }));
}


// ── Store-scoped reference data ─────────────────────────────────────
// Suppliers and calendars are per-store. The
// `{ storeId }` key segment makes them eligible for the targeted
// removal in clearStoreScopedQueries() on store switch.
//
// `enabled: !!storeId` prevents a fetch attempt before AuthContext
// has resolved the selected store on first mount. Once storeId
// transitions from undefined → string, the query fires.

export function useSuppliers() {
  const { selectedStore } = useAuth();
  const storeId = selectedStore?.id;
  return adapt(useQuery({
    queryKey: suppliersKey.all(storeId),
    queryFn: makePagedFetcher('/api/suppliers/', 'suppliers'),
    staleTime: FIVE_MIN,
    enabled: !!storeId,
  }));
}

export function useCalendars() {
  const { selectedStore } = useAuth();
  const storeId = selectedStore?.id;
  return adapt(useQuery({
    queryKey: calendarsKey.all(storeId),
    // Paged: `/api/calendars/` is a DRF-paginated endpoint, and one page of it
    // is not every calendar.
    queryFn: makePagedFetcher('/api/calendars/', 'calendars'),
    staleTime: FIVE_MIN,
    enabled: !!storeId,
  }));
}

